import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { transactionsApi, subdivideApi } from '../../lib/api';
import { TRANSACTION_CATEGORY_GROUPS, CATEGORY_TO_BUCKET_MAP, TRANSACTION_TYPES, TRANSACTION_TYPES_VISIBLE, aliasLegacyCategory, formatCurrency } from '../../lib/utils';
import { Receipt } from 'lucide-react';
import { toast } from 'sonner';

// Wave-4 follow-up: removed `mode` parameter (was 'purchase' | 'emd' | 'expense').
// The dedicated "Record Purchase" / "Record EMD" buttons were retired so all
// transaction entry flows through this single dialog with the user picking the
// type from the dropdown. Single source of truth for transaction creation.

// Map a free-form transaction category to one of the JV expense buckets.
// Authoritative mapping is CATEGORY_TO_BUCKET_MAP in lib/utils.js (auto-derived
// from the grouped list).
export const BUCKET_OPTIONS = [
  { value: 'purchase', label: 'Purchase Price' },
  { value: 'closing', label: 'Purchase Closing Costs' },
  { value: 'dd', label: 'Due Diligence' },
  { value: 'improvements', label: 'Improvements' },
  { value: 'selling', label: 'Selling Expenses (sale-side)' },
  { value: 'emergency', label: 'Emergency Advance (Senior — repaid before investor hurdle)' },
];

const autoBucketFromCategory = (category, type) => {
  // Purchase_Price + EMD always live in the 'purchase' bucket — the JV's
  // purchase_split % determines who's responsible.
  if (type === 'Purchase_Price' || type === 'EMD') return 'purchase';
  if (!['Expense', 'Reimbursement', 'Refund', 'Vendor_Reimbursement'].includes(type || 'Expense')) return '';
  return CATEGORY_TO_BUCKET_MAP[category] || CATEGORY_TO_BUCKET_MAP[aliasLegacyCategory(category)] || '';
};

export const TransactionFormDialog = ({
  open,
  onOpenChange,
  projectId,
  transaction,
  jvAgreements = [],
  isSubdivide = false,
  onSuccess
}) => {
  const isEditing = !!transaction;

  const [formData, setFormData] = useState({
    type: 'Expense',
    category: '',
    amount: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    responsible_party: '',
    received_by: '',
    jv_agreement_id: '',
    qbo_ref_id: '',
    expense_bucket: '',
    is_emergency_advance: false,
    lot_id: '',
    lot_ids: [],
    partner_id: '',
  });
  const [loading, setLoading] = useState(false);
  const [lots, setLots] = useState([]);
  // Multi-investor purchase split (Purchase_Price only).
  const [splitMode, setSplitMode] = useState(false);
  const [splitAmounts, setSplitAmounts] = useState({});

  // Subdivide deals: load the lot roster so costs can be tagged to a specific
  // lot (or left as "Global Allocation" → split pro-rata by acreage).
  useEffect(() => {
    if (open && isSubdivide && projectId) {
      subdivideApi.getLots(projectId)
        .then((r) => setLots(r.data?.lots || []))
        .catch((e) => { console.error('Failed to load lots', e); setLots([]); });
    }
  }, [open, isSubdivide, projectId]);

  // Reset form when dialog opens or transaction changes
  useEffect(() => {
    setSplitMode(false);
    setSplitAmounts({});
    if (transaction) {
      setFormData({
        type: transaction.type || 'Expense',
        category: transaction.category || '',
        amount: transaction.amount?.toString() || '',
        description: transaction.description || '',
        date: transaction.date?.split('T')[0] || new Date().toISOString().split('T')[0],
        responsible_party: transaction.responsible_party || '',
        received_by: transaction.received_by || '',
        jv_agreement_id: transaction.jv_agreement_id || '',
        qbo_ref_id: transaction.qbo_ref_id || '',
        expense_bucket: transaction.expense_bucket || autoBucketFromCategory(transaction.category, transaction.type),
        is_emergency_advance: !!transaction.is_emergency_advance,
        lot_id: transaction.lot_id || '',
        lot_ids: transaction.lot_ids || [],
        partner_id: transaction.partner_id || '',
      });
    } else if (open) {
      setFormData({
        type: 'Expense',
        category: '',
        amount: '',
        description: '',
        date: new Date().toISOString().split('T')[0],
        responsible_party: '',
        received_by: '',
        jv_agreement_id: '',
        qbo_ref_id: '',
        expense_bucket: '',
        is_emergency_advance: false,
        lot_id: '',
        lot_ids: [],
        partner_id: '',
      });
    }
  }, [transaction, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Multi-investor Purchase Price split: create one tagged Purchase_Price
    // per investor via the atomic backend endpoint (replaces any existing PP).
    if (splitMode && formData.type === 'Purchase_Price' && !isEditing) {
      const allocations = coInvestorOptions
        .map((ci) => ({ partner_id: ci.id, amount: parseFloat(splitAmounts[ci.id]) || 0 }))
        .filter((a) => a.amount > 0);
      if (allocations.length === 0) {
        toast.error('Enter at least one investor amount greater than zero.');
        return;
      }
      setLoading(true);
      try {
        const res = await transactionsApi.purchaseSplit({
          project_id: projectId,
          jv_agreement_id: jvAgreements[0]?.id || null,
          date: formData.date,
          description: formData.description || null,
          expense_bucket: formData.expense_bucket || 'purchase',
          allocations,
        });
        toast.success(`Purchase split across ${allocations.length} investors (${formatCurrency(res.data.purchase_price_total)})`);
        onSuccess({ type: 'Purchase_Price', isNew: true });
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to record purchase split');
      } finally {
        setLoading(false);
      }
      return;
    }

    setLoading(true);

    try {
      const isCashPos = ['Investor_Contribution', 'Manager_Advance', 'Investor_Advance'].includes(formData.type);
      const payload = {
        ...formData,
        project_id: projectId,
        amount: parseFloat(formData.amount),
        responsible_party: formData.responsible_party === 'none' ? null : (formData.responsible_party || null),
        received_by: formData.received_by === 'none' ? null : (formData.received_by || null),
        jv_agreement_id: formData.jv_agreement_id === 'none' ? null : (formData.jv_agreement_id || null),
        qbo_ref_id: formData.qbo_ref_id || null,
        // Cash-position transactions don't have a category by design
        category: isCashPos ? (formData.type.replace(/_/g, ' ')) : formData.category,
        // Expense bucket only applies to expense-style transactions
        expense_bucket: isCashPos ? null : (formData.expense_bucket || null),
        // Senior preferred emergency advance flag — auto-true when bucket=emergency.
        is_emergency_advance: !isCashPos && (
          formData.is_emergency_advance === true ||
          formData.expense_bucket === 'emergency'
        ),
        // Subdivide cost assignment: tag to a specific lot, or "global"
        // (Global Allocation — split pro-rata by acreage). Null on non-subdivide
        // deals and on cash-position entries (those tag a partner, not a lot).
        lot_id: (isSubdivide && !isCashPos)
          ? ((formData.lot_ids && formData.lot_ids.length > 1) ? null : (formData.lot_id || 'global'))
          : null,
        // Multi-lot cost assignment: allocate across a subset of lots (pro-rata
        // by acreage). Only sent when 2+ specific lots are selected.
        lot_ids: (isSubdivide && !isCashPos && formData.lot_ids && formData.lot_ids.length > 1) ? formData.lot_ids : null,
        // Investor attribution (multi-investor deals): tag the transaction to
        // the specific pooled co-investor who funded it. Independent of the
        // pooled profit split — used for per-investor contribution tracking.
        partner_id: (formData.partner_id && formData.partner_id !== 'none') ? formData.partner_id : null,
      };

      if (isCashPos && !payload.jv_agreement_id) {
        toast.error('Please select an investor (JV agreement) for this cash-position entry');
        setLoading(false);
        return;
      }

      if (isEditing) {
        await transactionsApi.update(transaction.id, payload);
        toast.success('Transaction updated successfully');
      } else {
        await transactionsApi.create(payload);
        toast.success('Transaction added successfully');
      }
      
      // Pass the transaction type back so parent can take action (e.g., prompt to change status)
      onSuccess({ type: formData.type, isNew: !isEditing });
    } catch (error) {
      console.error('Failed to save transaction:', error);
      const errorMessage = error.response?.data?.detail || 'Failed to save transaction';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Show responsible party for expense types
  // Cash-position transactions only need: amount, jv_agreement_id, date, description.
  // They are NOT counted in project P&L — they're settlement markers between
  // the investor and Land Finder.
  const isCashPositionType = ['Investor_Contribution', 'Manager_Advance', 'Investor_Advance'].includes(formData.type);

  const showResponsibleParty = ['Expense', 'Capital_In', 'Purchase_Price', 'EMD', 'Pre_Sale_Payment'].includes(formData.type) && !isCashPositionType;
  
  // Show category selector for expenses, reimbursements/refunds, AND sale revenue
  const showCategory = ['Expense', 'Reimbursement', 'Refund', 'Vendor_Reimbursement', 'Sale_Revenue'].includes(formData.type) && !isCashPositionType;
  
  // Show "Who Received" for reimbursements/refunds AND sale revenue
  const showReceivedBy = ['Reimbursement', 'Refund', 'Vendor_Reimbursement', 'Sale_Revenue'].includes(formData.type) && !isCashPositionType;

  // Multi-investor deals: investors pooled under one JV agreement. When any JV
  // on this deal has co-investors defined, offer a selector to attribute the
  // transaction to the specific investor who funded it (sets partner_id).
  const coInvestorOptions = (jvAgreements || []).flatMap((jv) =>
    (jv.co_investors || []).map((ci) => ({ id: ci.id, name: ci.name, jv_id: jv.id, capital_committed: ci.capital_committed || 0 }))
  );
  const splitTotal = coInvestorOptions.reduce((s, ci) => s + (parseFloat(splitAmounts[ci.id]) || 0), 0);

  // Multi-lot cost assignment: which lots this cost is assigned to (empty = Global).
  const selectedLotIds = (formData.lot_ids && formData.lot_ids.length > 0)
    ? formData.lot_ids
    : (formData.lot_id && formData.lot_id !== 'global' ? [formData.lot_id] : []);
  const isGlobalLot = selectedLotIds.length === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Receipt className="w-5 h-5 text-forest-600" />
            {isEditing ? 'Edit Transaction' : 'Add Transaction'}
          </DialogTitle>
          {!isEditing && (
            <DialogDescription>
              Record any transaction on this project — Purchase Price, EMD, Expense, Reimbursement, Refund, or Investor Wire. Pick the type below.
            </DialogDescription>
          )}
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Type</Label>
              <Select
                value={formData.type}
                onValueChange={(value) => {
                  const updates = { ...formData, type: value };
                  if (value === 'Reimbursement') updates.responsible_party = '';
                  // Sale Revenue defaults to received by Land Finder
                  if (value === 'Sale_Revenue') updates.received_by = 'Land_Finder';
                  // Re-derive bucket when type changes (Purchase_Price/EMD → 'purchase')
                  updates.expense_bucket = autoBucketFromCategory(updates.category, value);
                  setFormData(updates);
                  // Multi-investor deals: default to the split grid when the
                  // user picks Purchase Price, pre-filling each investor's
                  // committed capital.
                  if (!isEditing && value === 'Purchase_Price' && coInvestorOptions.length > 0) {
                    setSplitMode(true);
                    setSplitAmounts(Object.fromEntries(
                      coInvestorOptions.map((ci) => [ci.id, ci.capital_committed ? String(ci.capital_committed) : ''])
                    ));
                  } else {
                    setSplitMode(false);
                  }
                }}
              >
                <SelectTrigger className="rounded-sm" data-testid="transaction-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {/* Show only the 6 user-visible types. The full
                      TRANSACTION_TYPES list (which includes Sale_Revenue,
                      Note_Payment, etc.) is reserved for legacy data and
                      programmatic flows — admins editing an old txn with a
                      hidden type still see it labeled correctly via the
                      isEditing branch below. */}
                  {(isEditing
                    ? TRANSACTION_TYPES.filter(t => t.visible || t.value === formData.type)
                    : TRANSACTION_TYPES_VISIBLE
                  ).map(type => (
                    <SelectItem key={type.value} value={type.value}>
                      <span className={type.color}>{type.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Category - show for expenses, reimbursements, and sale revenue.
                Wave 3: Categories are now grouped by bucket. Picking a
                category auto-sets the bucket so the user only has to think
                about ONE thing — what kind of cost this is. */}
            {showCategory && (
              <div className="space-y-2">
                <Label>{showReceivedBy && formData.type !== 'Sale_Revenue' ? 'Refund Category' : 'Category'}</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({
                    ...formData,
                    category: value,
                    // Auto-set the bucket from the canonical category map.
                    // User can still override the bucket below if needed.
                    expense_bucket: autoBucketFromCategory(value, formData.type),
                  })}
                >
                  <SelectTrigger className="rounded-sm" data-testid="transaction-category-select">
                    <SelectValue placeholder="Choose a category..." />
                  </SelectTrigger>
                  <SelectContent className="max-h-96">
                    {/* Bucket-grouped list. Each group has a non-selectable
                        header (the bucket label) and the items underneath. */}
                    {TRANSACTION_CATEGORY_GROUPS.map(group => (
                      <React.Fragment key={`group-${group.label}`}>
                        <div className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground bg-muted/40 sticky top-0">
                          {group.label}
                        </div>
                        {group.items.map(cat => (
                          <SelectItem key={cat} value={cat} className="pl-4" data-testid={`transaction-category-option-${cat.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                            {cat}
                          </SelectItem>
                        ))}
                      </React.Fragment>
                    ))}
                  </SelectContent>
                </Select>
                {/* Show which bucket the category will auto-assign to */}
                {formData.category && CATEGORY_TO_BUCKET_MAP[formData.category] !== undefined && (
                  <p className="text-[11px] text-muted-foreground">
                    Auto-bucket: <strong>
                      {CATEGORY_TO_BUCKET_MAP[formData.category] || 'none (not bucketed)'}
                    </strong>
                    {CATEGORY_TO_BUCKET_MAP[formData.category] && (
                      <> — JV's <code>{CATEGORY_TO_BUCKET_MAP[formData.category]}_split %</code> determines who's responsible.</>
                    )}
                  </p>
                )}
                {/* Legacy categories: show alias hint if user is editing an
                    old category that has a Wave-3 canonical replacement. */}
                {formData.category && aliasLegacyCategory(formData.category) !== formData.category && (
                  <p className="text-[11px] text-amber-700">
                    Legacy category — consider re-saving as{' '}
                    <button
                      type="button"
                      className="underline font-semibold"
                      onClick={() => setFormData({
                        ...formData,
                        category: aliasLegacyCategory(formData.category),
                        expense_bucket: autoBucketFromCategory(aliasLegacyCategory(formData.category), formData.type),
                      })}
                      data-testid="transaction-legacy-alias-btn"
                    >
                      {aliasLegacyCategory(formData.category)}
                    </button>
                  </p>
                )}
                {showReceivedBy && formData.type !== 'Sale_Revenue' && (
                  <p className="text-xs text-muted-foreground">
                    Select what expense category this refund applies to.
                  </p>
                )}
              </div>
            )}

            {/* Who Received - show for reimbursements/refunds and sale revenue */}
            {showReceivedBy && (
              <div className="space-y-2">
                <Label>{formData.type === 'Sale_Revenue' ? 'Received By' : 'Who Received the Refund?'}</Label>
                <Select
                  value={formData.received_by || 'none'}
                  onValueChange={(value) => setFormData({ ...formData, received_by: value === 'none' ? '' : value })}
                >
                  <SelectTrigger className="rounded-sm" data-testid="transaction-received-by-select">
                    <SelectValue placeholder="Select recipient..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-- Not specified --</SelectItem>
                    <SelectItem value="Land_Finder">
                      <span className="text-forest-700">Land Finder</span>
                    </SelectItem>
                    <SelectItem value="Investor">
                      <span className="text-blue-700">Investor</span>
                    </SelectItem>
                  </SelectContent>
                </Select>
                {formData.type === 'Sale_Revenue' ? (
                  <p className="text-xs text-muted-foreground">
                    Who received this revenue? Defaults to Land Finder.
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    If investor funded but Land Finder received the refund, the investor is still owed this amount
                  </p>
                )}
              </div>
            )}

            {/* Expense Bucket - controls JV manager-advance math when paired
                with an investor JV that has bucket splits configured. */}
            {showCategory && !['Sale_Revenue'].includes(formData.type) && (
              <div className="col-span-2 space-y-2">
                <Label>Expense Bucket (for JV split math)</Label>
                <Select
                  value={formData.expense_bucket || 'none'}
                  onValueChange={(value) => setFormData({
                    ...formData,
                    expense_bucket: value === 'none' ? '' : value,
                    // Auto-flag emergency when admin picks the emergency bucket.
                    is_emergency_advance: value === 'emergency' ? true : formData.is_emergency_advance,
                  })}
                >
                  <SelectTrigger className="rounded-sm" data-testid="transaction-expense-bucket-select">
                    <SelectValue placeholder="Select bucket..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-- Not bucketed (no split applies) --</SelectItem>
                    {BUCKET_OPTIONS.map(b => (
                      <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  When the investor's JV has split %s set, LF-paid expenses become Manager Preferred Reimbursements
                  proportionally and are repaid from sale proceeds AFTER any senior emergency advance, BEFORE profit split.
                  Selling Expenses come off the top of the waterfall (no responsibility math).
                </p>
                <p className="text-[11px] text-muted-foreground italic">
                  <strong>Tip — Seller Tax Credit / closing credits:</strong> record as a{' '}
                  <code>Reimbursement</code> with bucket <code>Closing</code>, received-by{' '}
                  <code>Land Finder</code>. This reduces LF's net closing burden so the SOP waterfall
                  computes the correct manager-reimbursement-owed at sale.
                </p>

                {/* Senior preferred emergency-advance flag — only meaningful
                    for LF-paid Expenses. Shows when bucket=emergency OR
                    admin manually toggles. Persists as is_emergency_advance
                    on the transaction record. */}
                {formData.type === 'Expense' && (
                  <label
                    className={`flex items-start gap-2 p-2 rounded-sm border cursor-pointer ${formData.is_emergency_advance ? 'border-rose-300 bg-rose-50' : 'border-slate-200 bg-slate-50/40'}`}
                    data-testid="transaction-emergency-advance-toggle"
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={!!formData.is_emergency_advance}
                      onChange={(e) => setFormData({ ...formData, is_emergency_advance: e.target.checked })}
                      data-testid="transaction-emergency-advance-checkbox"
                    />
                    <div className="flex-1">
                      <span className="text-xs font-semibold text-rose-900">
                        Senior Manager Emergency Advance
                      </span>
                      <span className="block text-[11px] text-rose-800 mt-0.5">
                        Flag for LF-fronted emergency outlays (well repair, lien payoff)
                        that aren't anyone's contractual responsibility. At sale, repaid
                        to LF <strong>SENIOR</strong> to the investor hurdle (Step 2 of the waterfall).
                        Does NOT enter per-party Contractual Hurdle math.
                      </span>
                    </div>
                  </label>
                )}
              </div>
            )}

            {/* For Purchase_Price / EMD: bucket is auto-set to 'purchase'
                but admins can override (rare cases, e.g., DD-bucket EMD). */}
            {(formData.type === 'Purchase_Price' || formData.type === 'EMD') && (
              <div className="col-span-2 space-y-2">
                <Label>JV Responsibility Bucket</Label>
                <Select
                  value={formData.expense_bucket || 'purchase'}
                  onValueChange={(value) => setFormData({ ...formData, expense_bucket: value })}
                >
                  <SelectTrigger className="rounded-sm" data-testid="transaction-purchase-bucket-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BUCKET_OPTIONS.map(b => (
                      <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Default: <strong>Purchase</strong>. The JV's <code>purchase_split %</code> determines
                  who's responsible (e.g., 100% investor → if LF paid, LF gets reimbursed at sale).
                  {formData.type === 'EMD' && (
                    <> EMD is a payment-timing record only — it does <strong>not</strong> add to the
                    cost basis (the project's Purchase Price already includes the deposit).</>
                  )}
                </p>
              </div>
            )}

            {!splitMode && (
              <div className="space-y-2">
                <Label>Amount ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  required
                  className="rounded-sm"
                  placeholder="0.00"
                  data-testid="transaction-amount-input"
                  autoFocus
                />
              </div>
            )}

            {splitMode && (
              <div className="col-span-2 space-y-2" data-testid="purchase-split-grid">
                <div className="flex items-center justify-between">
                  <Label>Split Purchase Price across investors</Label>
                  <button
                    type="button"
                    className="text-xs text-muted-foreground underline"
                    onClick={() => setSplitMode(false)}
                    data-testid="purchase-split-disable"
                  >
                    Enter as a single amount instead
                  </button>
                </div>
                <div className="space-y-2 rounded-sm border p-2 bg-muted/20">
                  {coInvestorOptions.map((ci) => (
                    <div key={ci.id} className="flex items-center gap-2" data-testid={`purchase-split-row-${ci.id}`}>
                      <span className="text-sm flex-1">{ci.name}</span>
                      <div className="relative w-40">
                        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">$</span>
                        <Input
                          type="number"
                          step="0.01"
                          value={splitAmounts[ci.id] || ''}
                          onChange={(e) => setSplitAmounts({ ...splitAmounts, [ci.id]: e.target.value })}
                          className="rounded-sm pl-5 text-sm text-right"
                          placeholder="0.00"
                          data-testid={`purchase-split-amount-${ci.id}`}
                        />
                      </div>
                    </div>
                  ))}
                  <div className="flex items-center justify-between border-t pt-2 mt-1">
                    <span className="text-sm font-medium">Total Purchase Price</span>
                    <span className="font-mono font-semibold" data-testid="purchase-split-total">
                      {formatCurrency(splitTotal)}
                    </span>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Creates one Purchase Price per investor and sets the project&apos;s purchase price to the total.
                  Any existing Purchase Price on this project will be replaced.
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label>Date</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="rounded-sm"
                data-testid="transaction-date-input"
              />
            </div>

            {/* Cost Assignment (Subdivide) — tag this cost to a specific lot or
                leave as Global Allocation (split pro-rata by acreage). */}
            {isSubdivide && lots.length > 0 && !isCashPositionType && (
              <div className="col-span-2 space-y-2">
                <Label>Cost Assignment (Lot)</Label>
                <div className="rounded-sm border divide-y" data-testid="transaction-lot-multiselect">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, lot_id: 'global', lot_ids: [] })}
                    className={`w-full flex items-center gap-2 p-2 text-left text-sm ${isGlobalLot ? 'bg-blue-50' : 'hover:bg-muted/40'}`}
                    data-testid="lot-option-global"
                  >
                    <span className={`h-3.5 w-3.5 rounded-full border shrink-0 ${isGlobalLot ? 'bg-blue-600 border-blue-600' : 'border-muted-foreground'}`} />
                    <span>Global Allocation — split across all lots by acreage</span>
                  </button>
                  {lots.map((l) => {
                    const checked = selectedLotIds.includes(l.lot_id);
                    const toggleLot = () => {
                      const set = new Set(selectedLotIds);
                      if (set.has(l.lot_id)) set.delete(l.lot_id); else set.add(l.lot_id);
                      const arr = Array.from(set);
                      if (arr.length === 0) setFormData({ ...formData, lot_id: 'global', lot_ids: [] });
                      else if (arr.length === 1) setFormData({ ...formData, lot_id: arr[0], lot_ids: [] });
                      else setFormData({ ...formData, lot_id: null, lot_ids: arr });
                    };
                    return (
                      <div
                        key={l.lot_id}
                        className={`flex items-center gap-2 p-2 text-sm ${checked ? 'bg-emerald-50' : 'hover:bg-muted/40'}`}
                        data-testid={`lot-option-${l.lot_id}`}
                      >
                        <Checkbox
                          id={`lot-cb-${l.lot_id}`}
                          checked={checked}
                          onCheckedChange={toggleLot}
                          data-testid={`lot-checkbox-${l.lot_id}`}
                        />
                        <label htmlFor={`lot-cb-${l.lot_id}`} className="cursor-pointer flex-1 select-none">
                          {l.name}{l.acres ? ` · ${l.acres} ac` : ''}
                        </label>
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-muted-foreground">
                  <strong>Global</strong> spreads the cost across every lot pro-rata by acreage.
                  Select <strong>one</strong> lot to assign the full cost to it, or check <strong>multiple</strong> lots
                  to split the cost across just those lots (pro-rata by acreage).
                </p>
              </div>
            )}

            {/* Who Paid */}
            {showResponsibleParty && !splitMode && (
              <div className="col-span-2 space-y-2">
                <Label>Who Paid for This?</Label>
                <Select
                  value={formData.jv_agreement_id || formData.responsible_party || 'none'}
                  onValueChange={(value) => {
                    const isInvestor = jvAgreements.some(jv => jv.id === value);
                    if (isInvestor) {
                      setFormData({ ...formData, responsible_party: 'Investor', jv_agreement_id: value });
                    } else {
                      setFormData({ ...formData, responsible_party: value === 'none' ? '' : value, jv_agreement_id: '' });
                    }
                  }}
                >
                  <SelectTrigger className="rounded-sm" data-testid="transaction-paid-by-select">
                    <SelectValue placeholder="Select who paid..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-- Not specified --</SelectItem>
                    <SelectItem value="Land_Finder">
                      <span className="text-forest-700">Land Finder</span>
                    </SelectItem>
                    {jvAgreements.length > 0 && (
                      <>
                        <SelectItem value="Investor" disabled className="text-muted-foreground text-xs">
                          -- Investors --
                        </SelectItem>
                        {jvAgreements.map(jv => (
                          <SelectItem key={jv.id} value={jv.id}>
                            <span className="text-blue-700">{jv.investor_name}</span>
                          </SelectItem>
                        ))}
                      </>
                    )}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {formData.type === 'Purchase_Price' && 'Select who funded the purchase.'}
                  {formData.type === 'EMD' && 'Select who put up the earnest money.'}
                  {formData.type === 'Expense' && 'Select who paid for this expense.'}
                </p>
              </div>
            )}

            {/* Cash-position JV picker — for Investor_Contribution / Manager_Advance / Investor_Advance */}
            {isCashPositionType && (
              <div className="col-span-2 space-y-2">
                <Label>Investor (JV Agreement)</Label>
                <Select
                  value={formData.jv_agreement_id || 'none'}
                  onValueChange={(value) => setFormData({
                    ...formData,
                    jv_agreement_id: value === 'none' ? '' : value,
                    responsible_party: value === 'none' ? '' : 'Investor',
                  })}
                >
                  <SelectTrigger className="rounded-sm" data-testid="transaction-jv-select">
                    <SelectValue placeholder="Select investor..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-- Select investor --</SelectItem>
                    {jvAgreements.map(jv => (
                      <SelectItem key={jv.id} value={jv.id}>
                        {jv.investor_name} (commitment: ${(jv.capital_contributed || 0).toLocaleString()})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {formData.type === 'Investor_Contribution' && 'Cash wired by the investor toward their JV commitment. Tracked separately from project expenses.'}
                  {formData.type === 'Manager_Advance' && 'Land Finder paid for something on the investor\'s behalf. Investor will reimburse at profit-split time.'}
                  {formData.type === 'Investor_Advance' && 'Investor over-paid. Land Finder will refund or credit at profit-split time.'}
                </p>
              </div>
            )}

            {coInvestorOptions.length > 0 && !splitMode && (
              <div className="col-span-2 space-y-2">
                <Label>Funding Investor (optional)</Label>
                <Select
                  value={formData.partner_id || 'none'}
                  onValueChange={(value) => setFormData({ ...formData, partner_id: value === 'none' ? '' : value })}
                >
                  <SelectTrigger className="rounded-sm" data-testid="transaction-funding-investor-select">
                    <SelectValue placeholder="Attribute to an investor..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-- Not attributed --</SelectItem>
                    {coInvestorOptions.map((ci) => (
                      <SelectItem key={ci.id} value={ci.id} data-testid={`funding-investor-option-${ci.id}`}>
                        {ci.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  This deal has multiple investors pooled on one JV agreement. Tag whose capital funded this transaction so each investor&apos;s contribution is tracked.
                </p>
              </div>
            )}

            <div className="col-span-2 space-y-2">
              <Label>Description (optional)</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="rounded-sm"
                placeholder={
                  formData.type === 'Purchase_Price' ? 'e.g., Funds wired to ABC Title Company' :
                  formData.type === 'EMD' ? 'e.g., EMD deposited with Smith Law Firm' :
                  'Add notes about this transaction'
                }
                rows={2}
                data-testid="transaction-description-input"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="rounded-sm"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="rounded-sm bg-forest-500 hover:bg-forest-600 text-white"
              data-testid="transaction-submit-btn"
            >
              {loading ? 'Saving...' : isEditing ? 'Update' : 'Add Transaction'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
